import { SocketSessionRetry } from '../../components/SocketSessionRetry';
/**
 * @page TournamentTimerPage
 * @description 計時器主控台（參照 UI-5-2 全螢幕計時器設計）
 * 即時計時器顯示 + 控制（開始/暫停/停止/升降 Level）
 * 含完整盲注資訊、玩家數、獎金池
 * 計時器狀態持久化於 DB，離開頁面再回來會恢復
 */

import { AppearanceToggle } from '../../components/layout/DesignBoundary';
import { BrandIcon } from '../../components/layout/BrandIcon';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTournamentConsole } from './useTournamentConsole';
import { formatTime } from '../../lib/format';
import { getDisplayLevel } from '@pokerroom/types';
import { useSiteName } from '../../hooks/useSiteName';
import { timeoutSignal } from '../../lib/timeoutSignal';

function useVenueLogo(): string | null {
  const [logo, setLogo] = useState<string | null>(null);
  useEffect(() => {
    const apiBase =
      (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';
    fetch(`${apiBase}/api/branding/tv_logo`, { signal: timeoutSignal(10000) })
      .then((r) => (r.ok ? (r.json() as Promise<{ updatedAt?: string }>) : null))
      .then((d) => {
        if (d) setLogo(`${apiBase}/api/branding/tv_logo/file?v=${d.updatedAt ?? ''}`);
      })
      .catch((err) => console.warn('[TimerPage] Failed to fetch logo:', err));
  }, []);
  return logo;
}

function useCurrentClock() {
  const [clock, setClock] = useState(() =>
    new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }),
  );
  useEffect(() => {
    const interval = setInterval(() => {
      setClock(new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  return clock;
}

type ManualCountField = 'entrants' | 'eliminated' | 'rebuys' | 'addons';

export default function TournamentTimerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clock = useCurrentClock();
  const logo = useVenueLogo();
  const siteName = useSiteName();
  const {
    terminal,
    tournament,
    actionError,
    setActionError,
    customSeconds,
    setCustomSeconds,
    adjustmentNotice,
    connected,
    sessionPaused,
    retrySocket,
    sendTimerAction,
    socketError,
    clearSocketError,
    restLevels,
    nextLevel,
    displayLevel,
    isRunning,
    remainingSeconds,
    isBreak,
    sb,
    bb,
    anteBba,
    nextSb,
    nextBb,
    nextAnteBba,
    manualMode,
    activePlayers,
    totalEntries,
    rebuyCount,
    addonCount,
    eliminatedCount,
    prizePool,
    isWarning,
    isActive,
    adjustmentMutation,
    handleAdjust,
    validCustomSeconds,
    adjustmentDisabled,
    countMutation,
    finalizeMutation,
    pauseMutation,
    resumeMutation,
    handleStart,
    handlePause,
    handleStop,
  } = useTournamentConsole(id, () => navigate(`/tournament/${id}`));

  return (
    <div className="min-h-screen bg-base-100 text-base-content flex flex-col overflow-x-clip">
      <SocketSessionRetry paused={sessionPaused} retry={retrySocket} />
      {(actionError || socketError) && (
        <div
          role="alert"
          className="shrink-0 flex items-center justify-between gap-3 bg-red-950 px-4 py-3 text-sm text-red-100"
        >
          <span>{actionError || socketError}</span>
          <button
            type="button"
            onClick={() => {
              setActionError('');
              clearSocketError();
            }}
            aria-label="關閉錯誤訊息"
            className="shrink-0 px-3 py-1"
          >
            關閉
          </button>
        </div>
      )}
      {/* ─── Header (8vh) ─── */}
      <header className="min-h-[48px] shrink-0 border-b border-base-300 flex flex-wrap items-center gap-2 px-3 py-2 sm:px-6 lg:px-12 justify-between bg-base-200">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            onClick={() => navigate(`/tournament/${id}`)}
            className="text-base-content/70 hover:text-base-content text-sm flex items-center gap-1"
          >
            <BrandIcon value="arrow_back" size={24} />
            Back
          </button>
          <div className="hidden sm:block w-px h-6 bg-base-300" />
          {logo ? (
            <img
              src={logo}
              alt="Logo"
              className="hidden sm:block h-[5vh] max-h-[36px] object-contain"
            />
          ) : (
            <BrandIcon value="style" size={24} />
          )}
          <span className="text-primary font-bold text-sm uppercase tracking-widest hidden lg:inline">
            {siteName}
          </span>
          <span className="hidden sm:inline text-base-content/40">|</span>
          <h2 className="text-sm sm:text-lg lg:text-2xl font-bold tracking-tight truncate min-w-0 flex-1">
            {tournament?.name ?? 'Loading...'}
          </h2>
          <AppearanceToggle />
        </div>
        <div className="flex items-center justify-end w-full sm:w-auto gap-4 lg:gap-12 text-sm lg:text-xl font-semibold">
          <div className="flex items-center gap-2">
            <BrandIcon value="groups" size={24} />
            <span>
              {activePlayers} / {totalEntries}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <BrandIcon value="payments" size={24} />
            <span className="text-primary">NT$ {prizePool.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-red-400'}`}
            />
            <span className="text-base-content/70 text-xs hidden lg:inline">
              {connected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </header>

      {/* ─── Main Area ─── */}
      <main className="flex-1 flex flex-col items-center justify-center gap-2 sm:gap-3 lg:gap-1 py-3 lg:py-1 px-3 sm:px-6 lg:px-20">
        {/* Level 指示器 */}
        <div className="text-center">
          {isBreak ? (
            <p className="text-info font-black text-3xl lg:text-4xl tracking-[0.3em] mb-2">BREAK</p>
          ) : (
            <p className="text-primary font-black text-3xl lg:text-4xl tracking-[0.2em] mb-2">
              LEVEL {displayLevel}
            </p>
          )}
          <div className={`h-1 w-24 mx-auto rounded-full ${isBreak ? 'bg-info' : 'bg-primary'}`} />
        </div>

        {/* 大計時器 */}
        <div className="flex flex-col items-center justify-center">
          <div
            className="text-[clamp(4rem,20vw,9rem)] sm:text-[clamp(5rem,18vh,13rem)] lg:text-[clamp(6rem,16vh,12rem)] leading-none font-bold tracking-tighter"
            style={{
              fontFamily: 'var(--font-display)',
              fontVariantNumeric: 'tabular-nums',
              color: isBreak
                ? 'var(--state-info)'
                : isWarning
                  ? 'var(--state-error)'
                  : 'var(--text-primary)',
              textShadow: 'none',
            }}
          >
            {formatTime(remainingSeconds)}
          </div>
          <div className="flex gap-16 sm:gap-32 lg:gap-40">
            <span className="text-base-content/70 uppercase tracking-widest text-sm lg:text-lg font-bold">
              MIN
            </span>
            <span className="text-base-content/70 uppercase tracking-widest text-sm lg:text-lg font-bold">
              SEC
            </span>
          </div>
        </div>

        {/* 盲注資訊 */}
        {!isBreak ? (
          <div className="w-full max-w-4xl grid grid-cols-3 gap-2 sm:gap-4 lg:gap-8">
            <div className="flex flex-col items-center bg-primary/5 border border-base-300/50 min-w-0 px-2 sm:px-6 py-2 sm:py-4 lg:py-3 rounded-xl">
              <span className="text-base-content/70 text-sm lg:text-xl font-bold mb-1 lg:mb-2 uppercase tracking-widest">
                SB
              </span>
              <span className="text-[clamp(1.25rem,6vw,3rem)] lg:text-5xl font-black break-all text-base-content">
                {sb.toLocaleString()}
              </span>
            </div>
            <div className="flex flex-col items-center bg-primary/10 border-2 border-primary/50 min-w-0 px-2 sm:px-6 py-2 sm:py-4 lg:py-3 rounded-xl">
              <span className="text-primary text-sm lg:text-xl font-bold mb-1 lg:mb-2 uppercase tracking-widest">
                BB
              </span>
              <span className="text-[clamp(1.5rem,7vw,3.5rem)] lg:text-6xl font-black break-all text-base-content">
                {bb.toLocaleString()}
              </span>
            </div>
            <div className="flex flex-col items-center bg-primary/5 border border-base-300/50 min-w-0 px-2 sm:px-6 py-2 sm:py-4 lg:py-3 rounded-xl">
              <span className="text-base-content/70 text-sm lg:text-xl font-bold mb-1 lg:mb-2 tracking-widest">
                Ante/BBA
              </span>
              <span className="text-[clamp(1.25rem,6vw,3rem)] lg:text-5xl font-black break-all text-base-content">
                {anteBba}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="inline-block px-10 py-4 bg-blue-500/10 border border-blue-500/30 rounded-full">
              <span className="text-base-content text-2xl lg:text-4xl font-bold">
                Resuming at{' '}
                <span className="text-primary">
                  Level{' '}
                  {nextLevel ? getDisplayLevel(restLevels, nextLevel.levelNo) : displayLevel + 1}
                </span>
              </span>
            </div>
          </div>
        )}
      </main>

      {/* ─── Controls + Footer ─── */}
      <div className="border-t border-base-300">
        {/* 手動計數列 —— 只有 manualCountMode 賽事看得到 */}
        {manualMode && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#544e3b]/40 border-b border-base-300">
            {(
              [
                {
                  field: 'entrants',
                  label: '人數',
                  value: totalEntries,
                  canMinus: totalEntries > eliminatedCount,
                },
                {
                  field: 'eliminated',
                  label: '淘汰',
                  value: eliminatedCount,
                  canMinus: eliminatedCount > 0,
                },
                { field: 'rebuys', label: 'Rebuy', value: rebuyCount, canMinus: rebuyCount > 0 },
                { field: 'addons', label: 'Addon', value: addonCount, canMinus: addonCount > 0 },
              ] as Array<{
                field: ManualCountField;
                label: string;
                value: number;
                canMinus: boolean;
              }>
            ).map((c) => (
              <div
                key={c.field}
                className="flex items-center justify-between gap-2 bg-base-100 px-2 py-2 sm:px-5 sm:py-3"
              >
                <button
                  type="button"
                  onClick={() => countMutation.mutate({ field: c.field, delta: -1 })}
                  disabled={terminal || !c.canMinus || countMutation.isPending}
                  aria-label={`${c.label} 減一`}
                  className="w-11 h-11 shrink-0 rounded-lg bg-base-300 text-base-content/70 text-2xl leading-none disabled:opacity-25 disabled:cursor-not-allowed active:bg-base-300"
                >
                  −
                </button>
                <div className="flex flex-col items-center min-w-0">
                  <span className="text-[10px] sm:text-xs uppercase tracking-widest text-base-content/70">
                    {c.label}
                  </span>
                  <span className="text-2xl sm:text-3xl font-bold tabular-nums leading-tight">
                    {c.value}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => countMutation.mutate({ field: c.field, delta: 1 })}
                  disabled={terminal || countMutation.isPending}
                  aria-label={`${c.label} 加一`}
                  className="w-11 h-11 shrink-0 rounded-lg bg-primary/20 text-primary text-2xl leading-none disabled:opacity-25 disabled:cursor-not-allowed active:bg-primary/30"
                >
                  +
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 控制按鈕列 */}
        {/* 控制列必須可換行：在 390px 上不換行的話總寬約 612px，而頁面根層是
            overflow-hidden，溢出的按鈕不只看不見、連捲都捲不到——實測開始鍵
            會落在 x=-47，操作員按不到。 */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-5 lg:gap-8 px-2 py-3 bg-[#393528]/10">
          {/* 降 Level */}
          <button
            disabled={terminal}
            onClick={() => sendTimerAction({ action: 'prev_level' })}
            className="w-11 h-11 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-base-300 text-base-content/70 hover:bg-base-300 flex items-center justify-center"
            title="Previous level"
          >
            <BrandIcon value="skip_previous" size={24} />
          </button>

          {/* 開始 */}
          <button
            onClick={handleStart}
            disabled={terminal || isRunning || resumeMutation.isPending}
            className={`w-14 h-14 sm:w-20 sm:h-20 lg:w-16 lg:h-16 rounded-full font-bold text-base transition-colors flex items-center justify-center shadow-lg ${
              isRunning
                ? 'bg-base-300 text-base-content/30 cursor-not-allowed'
                : 'bg-success hover:bg-success text-white shadow-emerald-500/30'
            }`}
            title="Start / Resume"
          >
            <BrandIcon value="play_arrow" size={24} />
          </button>

          {/* 暫停 */}
          <button
            onClick={handlePause}
            disabled={terminal || !isRunning || pauseMutation.isPending}
            className={`w-14 h-14 sm:w-20 sm:h-20 lg:w-16 lg:h-16 rounded-full font-bold text-base transition-colors flex items-center justify-center shadow-lg ${
              !isRunning
                ? 'bg-base-300 text-base-content/30 cursor-not-allowed'
                : 'bg-warning hover:bg-warning text-white shadow-yellow-500/30'
            }`}
            title="Pause"
          >
            <BrandIcon value="pause" size={24} />
          </button>

          {/* 升 Level */}
          <button
            disabled={terminal}
            onClick={() => sendTimerAction({ action: 'next_level' })}
            className="w-11 h-11 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-base-300 text-base-content/70 hover:bg-base-300 flex items-center justify-center"
            title="Next level"
          >
            <BrandIcon value="skip_next" size={24} />
          </button>

          {/* 分隔 */}
          <div className="hidden sm:block w-px h-12 bg-[#544e3b] mx-1 lg:mx-3" />

          {/* 停止（結束賽事） */}
          {isActive && (
            <button
              onClick={handleStop}
              disabled={terminal || finalizeMutation.isPending}
              className="w-11 h-11 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-amber-600/20 text-warning hover:bg-amber-600/40 flex items-center justify-center"
              title="確認賽事結束（進入結算模式）"
            >
              <BrandIcon value="stop" size={24} />
            </button>
          )}
        </div>

        <section
          aria-label="調整剩餘時間"
          className="border-t border-base-300 bg-base-200 px-3 py-3"
        >
          <div className="mx-auto grid max-w-4xl gap-2 sm:grid-cols-2 sm:gap-x-4">
            <div className="flex flex-wrap items-center justify-between gap-1 text-sm sm:col-span-2">
              <h3 className="font-bold">調整剩餘時間</h3>
              <span role="status" className="text-base-content/70">
                {adjustmentMutation.isPending ? '調整中…' : adjustmentNotice || '保留目前 Level'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:items-stretch">
              {[-60, -180, -300, 60, 180, 300].map((delta) => (
                <button
                  key={delta}
                  type="button"
                  onClick={() => handleAdjust(delta)}
                  disabled={terminal || adjustmentDisabled}
                  aria-label={`${delta > 0 ? '增加' : '減少'} ${Math.abs(delta) / 60} 分鐘`}
                  className="min-h-11 rounded-lg bg-primary/10 px-2 font-bold text-primary disabled:opacity-40"
                >
                  {delta > 0 ? '+' : '−'}
                  {Math.abs(delta) / 60} 分
                </button>
              ))}
            </div>
            <div>
              <div className="flex items-end gap-2">
                <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-base-content/70">
                  自訂秒數
                  <input
                    aria-label="自訂秒數"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    value={customSeconds}
                    onChange={(event) => setCustomSeconds(event.target.value)}
                    placeholder="例如 30"
                    className="min-h-11 w-full rounded-lg border border-base-300 bg-base-100 px-3 text-base text-base-content"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => handleAdjust(-Number(customSeconds))}
                  disabled={terminal || adjustmentDisabled || !validCustomSeconds}
                  aria-label="減少自訂秒數"
                  className="min-h-11 shrink-0 rounded-lg bg-base-300 px-4 font-bold disabled:opacity-40"
                >
                  − 秒
                </button>
                <button
                  type="button"
                  onClick={() => handleAdjust(Number(customSeconds))}
                  disabled={terminal || adjustmentDisabled || !validCustomSeconds}
                  aria-label="增加自訂秒數"
                  className="min-h-11 shrink-0 rounded-lg bg-primary/20 px-4 font-bold text-primary disabled:opacity-40"
                >
                  + 秒
                </button>
              </div>
              <details className="text-sm text-base-content/70">
                <summary className="flex min-h-11 cursor-pointer items-center">
                  直接設定剩餘時間
                </summary>
                <div className="grid grid-cols-4 gap-2">
                  {[5, 10, 15, 20].map((min) => (
                    <button
                      key={min}
                      type="button"
                      onClick={() => sendTimerAction({ action: 'set_time', seconds: min * 60 })}
                      disabled={terminal || adjustmentMutation.isPending}
                      className="min-h-11 rounded-lg bg-base-300 px-2 font-medium disabled:opacity-40"
                    >
                      設為 {min} 分
                    </button>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </section>

        {/* 下一級盲注資訊 + 時鐘 */}
        <div className="min-h-11 flex items-center px-3 py-2 lg:px-12 bg-[#393528]/20">
          <div className="flex flex-wrap items-center gap-2 sm:gap-6 w-full">
            {nextLevel && !nextLevel.isBreak && nextBb ? (
              <>
                <div className="flex items-center gap-3 bg-primary text-black px-3 py-1 rounded font-black text-sm">
                  <BrandIcon value="fast_forward" size={24} />
                  NEXT
                </div>
                <div className="flex flex-wrap items-center gap-2 lg:gap-8 text-sm lg:text-xl font-bold">
                  <span className="text-base-content/70">
                    Level {getDisplayLevel(restLevels, nextLevel.levelNo)}
                  </span>
                  <div className="flex flex-wrap items-center gap-2 lg:gap-6 text-base-content">
                    <span>
                      <span className="text-primary text-xs">SB</span> {nextSb?.toLocaleString()}
                    </span>
                    <div className="w-px h-5 bg-[#544e3b]" />
                    <span>
                      <span className="text-primary text-xs">BB</span> {nextBb?.toLocaleString()}
                    </span>
                    {nextAnteBba && (
                      <>
                        <div className="w-px h-5 bg-[#544e3b]" />
                        <span>
                          <span className="text-primary text-xs">Ante/BBA</span> {nextAnteBba}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : nextLevel?.isBreak ? (
              <span className="text-info font-bold">
                Next: Break{' '}
                {nextLevel.durationSecs
                  ? (() => {
                      const m = Math.floor(nextLevel.durationSecs! / 60);
                      const s = nextLevel.durationSecs! % 60;
                      return s === 0 ? `${m} min` : `${m}m ${s}s`;
                    })()
                  : '?'}
              </span>
            ) : null}
            <div className="ml-auto flex items-center gap-3 text-base-content/70 text-base">
              <BrandIcon value="schedule" size={24} />
              <span>{clock}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
